import React, { useEffect, useRef } from 'react';

// 本地静态资源路径
const CKE4_CDN = '/ckeditor/ckeditor.js';

const CKE4_BASE = '/ckeditor/';

/**
 * CKE4Editor
 * props:
 * - value: string 初始/外部内容
 * - onChange: (data: string)=>void 内容变化回调
 * - placeholder: string 占位
 * - uploadBase: string 可选，后端 API 基址（默认同源）
 */
export default function CKE4Editor({ value, onChange, placeholder, uploadBase = '' }) {
  const textareaRef = useRef(null);
  const editorRef = useRef(null);

  useEffect(() => {
    let mounted = true;

    function loadScript() {
      return new Promise((resolve) => {
        // 指定 CKEditor 资源根路径，确保插件/皮肤正确加载
        if (!window.CKEDITOR_BASEPATH) {
          window.CKEDITOR_BASEPATH = CKE4_BASE;
        }
        
        if (window.CKEDITOR) return resolve();
        const exist = document.querySelector(`script[src="${CKE4_CDN}"]`);
        if (exist) {
          exist.addEventListener('load', () => resolve(), { once: true });
          exist.addEventListener('error', () => resolve(), { once: true });
          return;
        }
        const s = document.createElement('script');
        s.src = CKE4_CDN;
        s.async = true;
        s.onload = () => resolve();
        s.onerror = () => resolve();
        document.body.appendChild(s);
      });
    }

    async function init() {
      await loadScript();
      if (!mounted || !window.CKEDITOR || !textareaRef.current) return;

      // 允许并保留尽可能多的样式（颜色、字号、背景、行高、缩进等）
      window.CKEDITOR.config.language = 'zh\u002dcn';
      window.CKEDITOR.config.allowedContent = true;
      window.CKEDITOR.config.extraAllowedContent = `
        p{line-height;margin;text-indent};
        span{color;background-color;font-size;font-family};
        h1 h2 h3 h4 h5 h6{line-height};
        table tr th td{background-color;color;text-align};
        div{line-height;margin;text-indent}
      `;

      // 启用所需插件（standard-all 已包含大部分）
      window.CKEDITOR.config.extraPlugins = 'uploadimage,pastefromword,clipboard,justify,colorbutton,font,stylescombo,div,tabletools';
      // 移除不需要或产生警告的插件
      window.CKEDITOR.config.removePlugins = 'easyimage,cloudservices,exportpdf';

      // 上传接口（图片与通用文件均启用上传页签）
      window.CKEDITOR.config.filebrowserUploadUrl = `${uploadBase}/api/ck4upload`;
      window.CKEDITOR.config.filebrowserImageUploadUrl = `${uploadBase}/api/ck4upload`;
      window.CKEDITOR.config.uploadUrl = `${uploadBase}/api/ck4upload`;
      // 粘贴截图自动上传接口
      window.CKEDITOR.config.clipboard_uploadUrl = `${uploadBase}/api/ck4upload`;
      window.CKEDITOR.config.clipboard_handleImages = true;

      // Word/Excel 粘贴尽量保留格式
      window.CKEDITOR.config.pasteFromWordRemoveFontStyles = false;
      window.CKEDITOR.config.pasteFromWordRemoveStyles = false;
      window.CKEDITOR.config.forcePasteAsPlainText = false;

      // 自动高度
      window.CKEDITOR.config.autoGrow_minHeight = 300;
      window.CKEDITOR.config.autoGrow_onStartup = true;

      // 字体与字号集合（可按需扩充）
      window.CKEDITOR.config.font_names = `
        宋体/SimSun; 黑体/SimHei; 微软雅黑/Microsoft YaHei; 仿宋/FangSong; 楷体/KaiTi;
        Noto Sans/Noto Sans; Noto Serif/Noto Serif; Arial/Arial, Helvetica, sans-serif;
        Times New Roman/Times New Roman, Times, serif; Roboto/Roboto, sans-serif;
        Meiryo/Meiryo, sans-serif; "Hiragino Sans"/Hiragino Sans, sans-serif;
        ` + (window.CKEDITOR.config.font_names || '');
      window.CKEDITOR.config.fontSize_sizes = '12/12px;13/13px;14/14px;16/16px;18/18px;20/20px;24/24px;28/28px;32/32px;36/36px;48/48px;64/64px;';

      // 行高/段前后距/首行缩进等常用样式集
      if (!window.CKEDITOR.stylesSet.registered['custom_styles']) {
        window.CKEDITOR.stylesSet.add('custom_styles', [
          // 行高
          { name: '行高 1.2', element: 'p', styles: { 'line-height': '1.2' } },
          { name: '行高 1.5', element: 'p', styles: { 'line-height': '1.5' } },
          { name: '行高 2.0', element: 'p', styles: { 'line-height': '2' } },
          { name: '行高 2.5', element: 'p', styles: { 'line-height': '2.5' } },
          { name: '行高 3.0', element: 'p', styles: { 'line-height': '3' } },
          // 段前/段后
          { name: '段前 8px', element: 'p', styles: { 'margin-top': '8px' } },
          { name: '段后 8px', element: 'p', styles: { 'margin-bottom': '8px' } },
          { name: '段前 16px', element: 'p', styles: { 'margin-top': '16px' } },
          { name: '段后 16px', element: 'p', styles: { 'margin-bottom': '16px' } },
          // 首行缩进
          { name: '首行缩进 2 字', element: 'p', styles: { 'text-indent': '2em' } },
          { name: '首行缩进 0', element: 'p', styles: { 'text-indent': '0' } }
        ]);
      }
      window.CKEDITOR.config.stylesSet = 'custom_styles';

      const editor = window.CKEDITOR.replace(textareaRef.current, {
        height: '520px',   // 字符串或数字(像素)
        placeholder: placeholder || '请输入内容...',
        // 更完整的工具栏（格式、样式、字体、字号、颜色、对齐、列表、缩进、表格等）
        toolbar: [
          { name: 'document', items: ['Source'] },
          { name: 'clipboard', items: ['Undo', 'Redo', 'Paste', 'PasteText', 'PasteFromWord'] },
          { name: 'styles', items: ['Styles', 'Format', 'Font', 'FontSize'] },
          { name: 'colors', items: ['TextColor', 'BGColor'] },
          { name: 'basicstyles', items: ['Bold', 'Italic', 'Underline', 'Strike', 'Subscript', 'Superscript', 'RemoveFormat'] },
          { name: 'paragraph', items: ['NumberedList', 'BulletedList', '-', 'Outdent', 'Indent', '-', 'Blockquote', '-', 'JustifyLeft', 'JustifyCenter', 'JustifyRight', 'JustifyBlock'] },
          { name: 'insert', items: ['Image', 'Table', 'HorizontalRule', 'SpecialChar'] },
          { name: 'links', items: ['Link', 'Unlink'] }
        ]
      });

      editorRef.current = editor;
      if (value) editor.setData(value);

      editor.on('change', () => {
        const data = editor.getData();
        onChange && onChange(data);
      });
    }

    init();

    return () => {
      mounted = false;
      if (editorRef.current) {
        editorRef.current.destroy();
        editorRef.current = null;
      }
    };
  }, [value, onChange, placeholder, uploadBase]);

  // 若外部需要主动重设内容，可暴露方法；当前去除双向同步避免光标跳动

  return (
    <div>
      {/* 顶部视频上传按钮已移至弹窗底部 */}
      <textarea ref={textareaRef} />
    </div>
  );
} 